// ═══════════════════════════════════════════════════════════════════════════
// FUSION D'ACTIONS — helpers purs (formatters + normalisation)
// Extrait de page-action-merge.ts (découpe god-module 2026-07).
// ═══════════════════════════════════════════════════════════════════════════
//
// Fonctions PURES sans état ni DOM : normalisation d'ids, init du résultat depuis
// la conservée, libellés/tooltips (user/projet/comité), lecture d'une liste RACI,
// égalité scalaire tolérante. Les types viennent de page-action-merge.ts en
// `import type` (érasé à la compilation → aucun cycle runtime).

import type {
  ListField,
  MergeAction,
  MergeCommittee,
  MergeProject,
  MergeUser,
  ResultState,
} from './page-action-merge.js';

export function _normalizeIds(v: unknown): number[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((x) => (typeof x === 'number' ? x : ((x as { id?: number }).id ?? 0)))
    .filter((n) => n > 0);
}

export function _initResultFromKeeper(k: MergeAction): ResultState {
  return {
    text: k.text ?? null,
    deadline: k.deadline ?? null,
    deadline_revised: k.deadline_revised ?? null,
    priority: k.priority ?? null,
    status_id: k.status_id ?? null,
    progress: k.progress ?? 0,
    approver_id: k.approver_id ?? null,
    performers: _normalizeIds(k.performers),
    consulted: _normalizeIds(k.consulted),
    informed: _normalizeIds(k.informed),
    project_ids: [...(k.project_ids ?? [])],
    committee_ids: [...(k.committee_ids ?? [])],
  };
}

// Label court d'un user : trigramme si dispo, sinon nom complet.
// Utilisé dans les chips R/C/I.
export function _userLabel(u: MergeUser | undefined): string {
  if (!u) return '?';
  return u.trigram || u.name;
}

// Tooltip d'un user au survol : nom complet + (trigramme) si distincts.
export function _userTitle(u: MergeUser | undefined): string {
  if (!u) return '';
  return u.trigram && u.trigram !== u.name ? `${u.name} (${u.trigram})` : u.name;
}

// Pour les utilisateurs dans des selects (résumé long).
export function _userFull(u: MergeUser | undefined): string {
  if (!u) return '?';
  return u.name + (u.trigram ? ` (${u.trigram})` : '');
}

// Label court d'un projet : uniquement le code (cf. user 2026-05-21).
export function _projectLabel(p: MergeProject | undefined): string {
  if (!p) return '?';
  return p.code || p.name;
}

// Tooltip projet : "<code> - <nom>".
export function _projectTitle(p: MergeProject | undefined): string {
  if (!p) return '';
  return p.code && p.name ? `${p.code} - ${p.name}` : p.code || p.name;
}

export function _committeeLabel(c: MergeCommittee | undefined): string {
  if (!c) return '?';
  return c.code;
}

export function _committeeTitle(c: MergeCommittee | undefined): string {
  if (!c) return '';
  return c.code && c.name ? `${c.code} - ${c.name}` : c.code || c.name;
}

export function _getList(field: ListField, a: MergeAction): number[] {
  switch (field) {
    case 'performers':
      return _normalizeIds(a.performers);
    case 'consulted':
      return _normalizeIds(a.consulted);
    case 'informed':
      return _normalizeIds(a.informed);
    case 'project_ids':
      return a.project_ids ?? [];
    case 'committee_ids':
      return a.committee_ids ?? [];
  }
}

// Égalité tolérante aux null/undefined pour comparer la valeur scalaire d'une
// source (keeper / absorbed) à la valeur courante du résultat.
export function _scalarEq(a: unknown, b: unknown): boolean {
  if (a == null && b == null) return true;
  return a === b;
}
